import { useMemo, useState, useEffect } from 'react';
import {
	neutralBlack, neutralWhite, neutralDisabled, grayMedium, grayLightBorder,
	medalGold, medalGoldMid, medalGoldDark, medalSilver, medalSilverDark,
	bronzeMain, bronzeLight, bronzeDark, bronzeMid, progressDanger, redTierDark, redTierMid,
} from '../themes/colors';
import { StyledContainer } from './PodiumAvatar.style';
/* eslint-disable no-magic-numbers */
/* eslint-disable max-len */
export type PodiumTier = 'gold' | 'silver' | 'bronze' | 'red';

export type PodiumAvatarColors = {
  glow: string;
  placeColor: string;
  placeNumber: string;
  placeBackgroundStop1: string;
  placeBackgroundStop2: string;
  avatarBackgroundStop1: string;
  avatarBackgroundStop2: string;

  sparkle1Stop1?: string;
  sparkle1Stop2?: string;
  sparkle1Stop3?: string;
};

const getPodiumTier = (placement: number): PodiumTier => {
	if (placement === 1) return 'gold';
	if (placement === 2) return 'silver';
	if (placement === 3) return 'bronze';
	return 'red'; 
};

const COLORS: Record<PodiumTier, PodiumAvatarColors> = {
	gold: {
		glow: medalGold,
		placeColor: medalGold,
		placeNumber: neutralBlack,
		placeBackgroundStop1: medalGold,
		placeBackgroundStop2: medalGoldMid,
		avatarBackgroundStop1: medalGold,
		avatarBackgroundStop2: medalGoldMid,
		sparkle1Stop1: medalGold,
		sparkle1Stop2: medalGoldMid,
		sparkle1Stop3: medalGoldDark,
	},
	silver: {
		glow: medalSilver,
		placeColor: medalSilver,
		placeNumber: medalSilverDark,
		placeBackgroundStop1: grayLightBorder,
		placeBackgroundStop2: neutralDisabled,
		avatarBackgroundStop1: grayLightBorder,
		avatarBackgroundStop2: neutralDisabled,
		sparkle1Stop1: grayLightBorder,
		sparkle1Stop2: neutralDisabled,
		sparkle1Stop3: grayMedium,
	},
	bronze: {
		glow: bronzeMain,
		placeColor: bronzeMain,
		placeNumber: neutralWhite,
		placeBackgroundStop1: bronzeLight,
		placeBackgroundStop2: bronzeDark,
		avatarBackgroundStop1: bronzeLight,
		avatarBackgroundStop2: bronzeDark,
		sparkle1Stop1: bronzeLight,
		sparkle1Stop2: bronzeMid,
		sparkle1Stop3: bronzeDark,
	},

	red: {
		glow: progressDanger,
		placeColor: progressDanger,
		placeNumber: neutralWhite,
		placeBackgroundStop1: progressDanger,
		placeBackgroundStop2: redTierDark,
		avatarBackgroundStop1: progressDanger,
		avatarBackgroundStop2: redTierDark,
		sparkle1Stop1: progressDanger,
		sparkle1Stop2: redTierMid,
		sparkle1Stop3: redTierDark,
	},
};

let __podiumAvatarId = 0;
const nextId = () => {
	__podiumAvatarId += 1;
	return `podium_${__podiumAvatarId}`;
};

function initials(name: string) {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	const first = parts[0]?.[0] ?? '';
	const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
	return (first + last).toUpperCase();
}

export type PodiumAvatarProps = {
  name: string;
  avatarUrl?: string;
  placement: number;
  size?: number;
  colors?: Partial<PodiumAvatarColors>;
  showSparkles?: boolean;
};

export const PodiumAvatar = ({
	name,
	avatarUrl,
	placement,
	size = 90,
	colors,
	showSparkles,
}: PodiumAvatarProps) => {
	const [loaded, setLoaded] = useState(false);
	const [imageError, setImageError] = useState(false);

	const uid = useMemo(() => nextId(), []);

	useEffect(() => {
		let cancelled = false;
 
		setLoaded(false);
		setImageError(false);

		if (!avatarUrl) return () => { cancelled = true; };
		const img = new window.Image();
	
		const handleLoad = () => {
			if (cancelled) return;
			setLoaded(true);
			setImageError(false);
		};
	
		const handleError = () => {
			if (cancelled) return;
			setLoaded(false);
			setImageError(true);
		};
	
		img.onload = handleLoad;
		img.onerror = handleError;
		img.src = avatarUrl;
	
		if (img.complete && img.naturalWidth > 0) {
			handleLoad();
		}
	
		return () => {
			cancelled = true;
			img.onload = null;
			img.onerror = null;
		};
	}, [avatarUrl]);

	const tier = getPodiumTier(placement);
	const baseColors = COLORS[tier];
	const c: PodiumAvatarColors = { ...baseColors, ...colors };

	const shouldShowSparkles = showSparkles ?? tier === 'gold';
	const showImage = Boolean(avatarUrl) && loaded && !imageError;

	const ids = {
		avatarBorder: `avatar_border_${uid}`,
		avatarBackground: `avatar_background_${uid}`,
		placeBorder: `place_border_${uid}`,
		placeFilter: `place_filter_${uid}`,
		clipCircle: `clipCircle_${uid}`,
		sparkle1: `sparkle_background_1_${uid}`,
		sparkle2: `sparkle_background_2_${uid}`,
		sparkle3: `sparkle_background_3_${uid}`,
	};

	return (
		<StyledContainer
			width={size}
			height={size}
			viewBox="0 0 186 209"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			role="img"
			aria-label={`${name} placement ${placement}`}
		>
			<ellipse
				cx="93"
				cy="93"
				rx="93"
				ry="93"
				transform="rotate(90 93 93)"
				fill={`url(#${ids.avatarBorder})`}
			/>

			<circle cx="93" cy="93" r="84" fill={`url(#${ids.avatarBackground})`} />

			<g filter={`url(#${ids.placeFilter})`}>
				<circle cx="93" cy="177" r="21" fill={c.glow} />
			</g>

			<clipPath id={ids.clipCircle}>
				<circle cx="93" cy="93" r="84" />
			</clipPath>

			{!showImage && (
				<text
					x="93"
					y="93"
					textAnchor="middle"
					dominantBaseline="central"
					fill={c.placeNumber}
					fontSize="70px"
					fontFamily="Red Hat Display, sans-serif"
					fontWeight={700}
				>
					{initials(name)}
				</text>
			)}

			{showImage && (
			// eslint-disable-next-line react/no-unknown-property
				<image
					x="9"
					y="9"
					width="168"
					height="168"
					href={avatarUrl}
					xlinkHref={avatarUrl}
					clipPath={`url(#${ids.clipCircle})`}
					preserveAspectRatio="xMidYMid slice"
				/>
			)}

			<circle
				cx="93"
				cy="177"
				r="19"
				fill={c.placeColor}
				stroke={`url(#${ids.placeBorder})`}
				strokeWidth="4"
			/>
			<text
				x="93"
				y="176"
				textAnchor="middle"
				dominantBaseline="central"
				fill={c.placeNumber}
				fontSize="22px"
				fontFamily="Red Hat Display, sans-serif"
				fontWeight={700}
			>
				{placement}
			</text>

			{shouldShowSparkles && (
				<>
					<path
						d="M145.292 146.289L135.641 142.686L132.037 130.774C131.898 130.315 131.476 130 131 130C130.524 130 130.102 130.315 129.963 130.774L126.36 142.686L116.708 146.289C116.282 146.449 116 146.857 116 147.315C116 147.772 116.282 148.182 116.708 148.341L126.355 151.943L129.96 164.218C130.097 164.682 130.52 165 131 165C131.479 165 131.903 164.682 132.04 164.217L135.645 151.942L145.293 148.34C145.717 148.182 146 147.773 146 147.315C146 146.858 145.717 146.449 145.292 146.289Z"
						fill={`url(#${ids.sparkle1})`}
					/>
					<path
						d="M136.783 193.55L131.668 192.864L130.295 187.373C130.025 186.641 129.781 186 129 186C128.221 186 127.822 186.641 127.55 187.373L126.177 192.864L121.217 193.684C120.484 193.956 120 194.221 120 195C120 195.779 120.484 196.113 121.217 196.383L126.177 197.668L127.55 202.783C127.822 203.516 128.221 204 129 204C129.779 204 130.069 203.516 130.341 202.783L131.668 197.668L136.783 196.295C137.516 196.023 138 195.779 138 195C138 194.221 137.516 193.822 136.783 193.55Z"
						fill={`url(#${ids.sparkle2})`}
					/>
					<path
						d="M39.5902 173.497L47.6328 170.512L50.636 160.642C50.7517 160.261 51.1032 160 51.5 160C51.8968 160 52.2483 160.261 52.364 160.642L55.3663 170.512L63.4098 173.497C63.765 173.629 64 173.967 64 174.347C64 174.726 63.765 175.066 63.4098 175.197L55.3708 178.181L52.3667 188.352C52.2528 188.736 51.9004 189 51.5 189C51.1005 189 50.7472 188.736 50.6333 188.351L47.6292 178.18L39.5893 175.196C39.2359 175.065 39 174.727 39 174.347C39 173.968 39.2359 173.629 39.5902 173.497Z"
						fill={`url(#${ids.sparkle3})`}
					/>
				</>
			)}

			<defs>
				<filter
					id={ids.placeFilter}
					x="61"
					y="145"
					width="64"
					height="64"
					filterUnits="userSpaceOnUse"
					colorInterpolationFilters="sRGB"
				>
					<feFlood floodOpacity="0" result="BackgroundImageFix" />
					<feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
					<feGaussianBlur stdDeviation="5.5" result="place_blur" />
				</filter>

				<radialGradient
					id={ids.avatarBorder}
					cx="0"
					cy="0"
					r="1"
					gradientUnits="userSpaceOnUse"
					gradientTransform="translate(186 92.6847) rotate(180) scale(186 230.192)"
				>
					<stop stopColor={c.avatarBackgroundStop1} />
					<stop offset="1" stopColor={c.avatarBackgroundStop2} />
				</radialGradient>

				<linearGradient id={ids.avatarBackground} x1="93" y1="9" x2="93" y2="177" gradientUnits="userSpaceOnUse">
					<stop stopColor={c.avatarBackgroundStop1} />
					<stop offset="1" stopColor={c.avatarBackgroundStop2} />
				</linearGradient>

				<linearGradient id={ids.placeBorder} x1="93" y1="156" x2="93" y2="198" gradientUnits="userSpaceOnUse">
					<stop stopColor={c.placeBackgroundStop1} />
					<stop offset="1" stopColor={c.placeBackgroundStop2} />
				</linearGradient>

				<linearGradient id={ids.sparkle1} x1="131" y1="130" x2="131" y2="165" gradientUnits="userSpaceOnUse">
					<stop stopColor={c.sparkle1Stop1 ?? c.avatarBackgroundStop1} />
					<stop offset="0.624369" stopColor={c.sparkle1Stop2 ?? c.avatarBackgroundStop2} />
					<stop offset="1" stopColor={c.sparkle1Stop3 ?? c.avatarBackgroundStop2} />
				</linearGradient>

				<linearGradient id={ids.sparkle2} x1="129" y1="186" x2="129" y2="204" gradientUnits="userSpaceOnUse">
					<stop stopColor={c.sparkle1Stop1 ?? c.avatarBackgroundStop1} />
					<stop offset="0.624369" stopColor={c.sparkle1Stop2 ?? c.avatarBackgroundStop2} />
					<stop offset="1" stopColor={c.sparkle1Stop3 ?? c.avatarBackgroundStop2} />
				</linearGradient>

				<linearGradient id={ids.sparkle3} x1="52.2274" y1="168.199" x2="51.5" y2="189" gradientUnits="userSpaceOnUse">
					<stop stopColor={c.sparkle1Stop1 ?? c.avatarBackgroundStop1} />
					<stop offset="0.624369" stopColor={c.sparkle1Stop2 ?? c.avatarBackgroundStop2} />
					<stop offset="1" stopColor={c.sparkle1Stop3 ?? c.avatarBackgroundStop2} />
				</linearGradient>
			</defs>
		</StyledContainer>
	);
};
